/**
 * Cultural Aspects Section Component
 * Displays cultural and social aspects with structured items
 * Follows Single Responsibility Principle - only responsible for rendering the section
 */

import React, { useMemo } from 'react';
import Image from 'next/image';
import { spacing, borders } from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { CulturalAspectList } from './CulturalAspectList';
import { CulturalAspectsSectionProps, CulturalAspect } from './types';
import { parseCulturalAspectItems } from './utils';

/**
 * Cultural Aspects Section Component
 * Renders a section with title, optional image, and list of cultural aspects
 * Each aspect has a bold heading and description
 * Supports both string[] (legacy) and CulturalAspect[] (structured) formats
 */
export const CulturalAspectsSection: React.FC<CulturalAspectsSectionProps> = ({
  title = 'Cultural & Social Aspects',
  items,
  image,
  className,
}) => {
  // Parse items - handle both string[] and CulturalAspect[] formats
  const parsedItems = useMemo<CulturalAspect[]>(() => {
    if (!items || items.length === 0) {
      return [];
    }

    // Check if items are already structured (CulturalAspect[])
    if (typeof items[0] === 'object' && 'heading' in items[0]) {
      return items as CulturalAspect[];
    }

    // Parse string items into structured format
    return parseCulturalAspectItems(items as string[]);
  }, [items]);

  if (parsedItems.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      <SectionHeader title={title} hasBackground={false} />
      {image && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 250,
            borderRadius: borders.radius.lg,
            overflow: 'hidden',
            padding: `0 ${spacing[3]}px`,
          }}
        >
          <Image src={image} alt={title} fill style={{ objectFit: 'cover' }} />
        </div>
      )}
      <div
        style={{
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
        }}
      >
        <CulturalAspectList items={parsedItems} />
      </div>
    </div>
  );
};
